import { useEffect, useMemo } from "react"
import Loader from "../../components/Loader"
import DateGroupDivider from "./DateGroupDivider"
import LogsSidebarRow from "./LogsSidebarRow"
import { parseMatchingVideos } from "./videoMetrics"

const keyboardKeys = [
  "ArrowDown",
  "ArrowUp",
  "Home",
  "End",
  "PageDown",
  "PageUp",
]

/*
  The master list: records grouped by date bucket, scrolling in its own pane.

  A listbox rather than a set of tabs — the detail pane is a view *of* the
  selected record, not a peer panel, and a listbox gives the whole list one tab
  stop. DOM focus stays on the list and `aria-activedescendant` names the
  selected row, so arrowing through 100 records never throws focus across the
  screen.

  Selection follows focus. That is the right listbox mode here because selecting
  is cheap and reversible; the queries it fires are cached, so scrubbing does not
  hammer the API.
*/
const LogsSidebar = ({
  groups,
  loadedCount,
  total,
  selectedId,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  scrollRef,
  listRef,
  sentinelRef,
  onSelect,
  onLoadMore,
}) => {
  const flatRows = useMemo(
    () => groups.flatMap((group) => group.rows),
    [groups]
  )

  /*
    `block: "nearest"` is what makes this safe to run on every selection change:
    a row that is already fully visible — which is always the case just after a
    click — does not move at all, so only keyboard and deep-link selections
    actually scroll. No smooth behaviour, which is what made the accordion this
    replaced need two staggered timers.
  */
  useEffect(() => {
    if (!selectedId) return
    listRef.current
      ?.querySelector(`[id="log-option-${selectedId}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [selectedId, listRef])

  const handleKeyDown = (event) => {
    if (!keyboardKeys.includes(event.key) || !flatRows.length) return
    event.preventDefault()

    const last = flatRows.length - 1
    const current = flatRows.findIndex(
      (row) => String(row.ReportVideosLogId) === selectedId
    )
    let next = current

    switch (event.key) {
      case "ArrowDown":
        next = current < 0 ? 0 : Math.min(last, current + 1)
        break
      case "ArrowUp":
        next = current < 0 ? last : Math.max(0, current - 1)
        break
      case "PageDown":
        next = current < 0 ? 0 : Math.min(last, current + 10)
        break
      case "PageUp":
        next = current < 0 ? last : Math.max(0, current - 10)
        break
      case "Home":
        next = 0
        break
      case "End":
        next = last
        break
      default:
        break
    }

    // Arrowing off the end pulls the next page in rather than dead-ending.
    if (
      event.key === "ArrowDown" &&
      current === last &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore()
    }

    if (next !== current) onSelect(flatRows[next])
  }

  return (
    <aside className="flex h-full w-[20%] min-w-[220px] shrink-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 text-[11px] text-gray-500">
        <span>
          Showing <b>{loadedCount}</b> of <b>{total}</b> records
        </span>
      </div>

      {/* The scrollport starts below the head, so the scrollbar sits inside the
          card and never crosses its rounded corners. It also means the date
          dividers stick to top-0 rather than under a header sharing the scroll. */}
      {/* relative: the rows inside carry a sr-only status span. sr-only is
          position:absolute with no inset values, and nothing between here and
          <body> is positioned (Navbar is `fixed`, but on a sibling subtree, not
          an ancestor of this pane) — so without a local containing block those
          spans lay out against the document root instead of this scrollport,
          inflating the whole PAGE's scroll height by however far the list
          scrolls internally. `relative` gives them one, with no visible effect. */}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col">
            {[1, 2, 3, 4, 5, 6].map((row) => (
              <div
                key={row}
                className="flex flex-col gap-2 border-b border-gray-200 px-3.5 py-3"
              >
                <div className="h-3.5 w-32 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-20 animate-pulse rounded-full bg-gray-100" />
              </div>
            ))}
          </div>
        ) : groups.length === 0 ? (
          <p className="px-3.5 py-6 text-center text-xs text-gray-500">
            No records found. Try adjusting your filters.
          </p>
        ) : (
          <ul
            ref={listRef}
            aria-activedescendant={
              selectedId ? `log-option-${selectedId}` : undefined
            }
            aria-label="Video log records"
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary"
            role="listbox"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            {groups.map((group) => (
              // Each divider lives inside the section holding its own rows, so it
              // gets pushed out by the next group's rather than stacking on it.
              <li key={group.group} aria-label={group.label} role="group">
                <DateGroupDivider label={group.label} />
                <ul role="none">
                  {group.rows.map((log) => (
                    <LogsSidebarRow
                      key={log.ReportVideosLogId}
                      isSelected={String(log.ReportVideosLogId) === selectedId}
                      log={log}
                      videos={parseMatchingVideos(log.MatchingVideos)}
                      onSelect={() => onSelect(log)}
                    />
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}

        <div ref={sentinelRef} className="h-1" />

        {hasNextPage && (
          <div className="flex justify-center p-3">
            {isFetchingNextPage ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader />
                <span>Loading more…</span>
              </div>
            ) : (
              <button
                className="rounded-md bg-blue-600 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                onClick={onLoadMore}
              >
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}

export default LogsSidebar
